import { ApdmImportPurpose, ApdmImportStatus } from './enums';

/** A field an import cannot do without and could not find by itself. */
export enum ApdmReadingGap {
  Plate = 'plate',
  ServiceDate = 'service_date',
  Items = 'items',
}

/** Why a row was left out of an import. */
export enum ApdmImportSkipReason {
  InvalidPlate = 'invalid_plate',
  MissingPlate = 'missing_plate',
  MissingServiceDate = 'missing_service_date',
  InvalidServiceDate = 'invalid_service_date',
  MissingDocumentId = 'missing_document_id',
  UnknownCompletion = 'unknown_completion',
  Other = 'other',
}

export type ApdmReadingAnswers = Partial<Record<ApdmReadingGap, number>>;

export interface ApdmImportOutcome {
  version: 2;
  rows: {
    total: number;
    kept: number;
    notCompleted: number;
    skipped: Partial<Record<ApdmImportSkipReason, number>>;
  };
  documents: {
    total: number;
    visits: number;
    washOnly: number;
    conflicts: number;
    withUnknownItem: number;
  };
  vehicles: number;
  routine: Record<string, number>;
  items: {
    total: number;
    unknown: number;
    topUnknown: Array<{ text: string; count: number }>;
  };
  dateRange: { from: string; through: string } | null;
  dues: { vehicles: number; overdue: number; within30Days: number };
  months: Array<{ month: string; visits: number }>;
  /** The file has no status column: every row counts as finished work. */
  completionAssumed: boolean;
  /** One row per vehicle: its visits are last visits, work unknown. */
  vehicleList: boolean;
  /** The file lists customers by plate alone: no visits. */
  contactList: boolean;
}

export interface ApdmImportColumnChoice {
  columnIndex: number;
  header: string;
  examples: string[];
  /** The asked gaps this column could answer; only these offer it. */
  fits: ApdmReadingGap[];
}

export interface ApdmImportQuestion {
  gaps: ApdmReadingGap[];
  /** Columns already taken for other fields; picking one moves it. */
  answers: Partial<Record<ApdmReadingGap, number>>;
  columns: ApdmImportColumnChoice[];
}

export interface ApdmImportAttention {
  skippedRate: number;
  unclassifiedRate: number;
  threshold: number;
  open: boolean;
}

/** Why an import stopped, as far as the user can act on it. */
export enum ApdmImportFailure {
  /** The file cannot be imported: delete it and upload another. */
  File = 'file',
  /** Publishing stopped part-way: start it again. */
  Publication = 'publication',
}

/** An import as the server shows it. */
export interface ApdmImportView {
  id: string;
  fileName: string;
  status: ApdmImportStatus;
  purpose: ApdmImportPurpose;
  createdAt: string;
  updatedAt: string | null;
  outcome: ApdmImportOutcome | null;
  attention: ApdmImportAttention | null;
  question: ApdmImportQuestion | null;
  failure: ApdmImportFailure | null;
}

export interface ApdmImportViewList {
  items: ApdmImportView[];
  nextAfterImportId: string | null;
}
